import { isBundle, isPlan, limitFor } from './limits';

/**
 * What the server needs: the file it parses, and the creatives to attach to the job.
 *
 * An archive is a plan with no creatives beside it — it carries its own, and `bundleWithOthers`
 * is what keeps anything else out — so it needs no arm of its own here. Where the difference
 * does matter, `isBundle` answers it from the file.
 */
export interface Selection {
  plan: File;
  creatives: File[];
}

/**
 * Why a selection was refused, as a code rather than a sentence.
 *
 * The wording lives with the dialog's other copy so it goes through `t()`; keeping this
 * module free of i18n is also what lets it be tested without a translation runtime.
 */
export type Refusal =
  | { reason: 'noPlan' }
  | { reason: 'severalPlans'; fileNames: string[] }
  | { reason: 'clashingNames'; fileNames: string[] }
  | { reason: 'bundleWithOthers' }
  | { reason: 'unsupported'; fileNames: string[] }
  | { reason: 'tooLarge'; files: { fileName: string; limitBytes: number }[] };

export type SplitResult = { ok: true; selection: Selection } | { ok: false; refusal: Refusal };

/**
 * Splits a dropped selection into the plan and the creatives that belong to it.
 *
 * The refusals are the point as much as the split is: the job record is written before the
 * plan is posted, so a selection that cannot be understood has to be caught here or it
 * strands a job nobody asked for.
 *
 * Checks run structure-first and size last. A selection with the wrong files in it is a
 * different mistake from one with a file that is merely too big, and naming the second while
 * the first is unresolved sends the operator after the wrong thing.
 */
export function splitSelection(files: File[]): SplitResult {
  const bundles = files.filter(isBundle);
  if (bundles.length > 0) {
    // Two archives are two plans, and they have to be named as that before the arm below,
    // which counts the selection without looking at what is in it: its advice is to drop the
    // creatives loose instead, and here there are none to drop.
    if (bundles.length > 1) {
      return {
        ok: false,
        refusal: { reason: 'severalPlans', fileNames: bundles.map((file) => file.name) },
      };
    }

    // An archive already carries its creatives and names them itself, so anything alongside
    // it is either a duplicate or a file the plan inside will never reference.
    if (files.length > 1) return { ok: false, refusal: { reason: 'bundleWithOthers' } };

    const [bundle] = bundles;
    return tooLarge([bundle]) ?? { ok: true, selection: { plan: bundle, creatives: [] } };
  }

  const unsupported = files.filter((file) => limitFor(file) === undefined);
  if (unsupported.length > 0) {
    return {
      ok: false,
      refusal: { reason: 'unsupported', fileNames: unsupported.map((file) => file.name) },
    };
  }

  const plans = files.filter(isPlan);
  if (plans.length === 0) return { ok: false, refusal: { reason: 'noPlan' } };
  if (plans.length > 1) {
    return {
      ok: false,
      refusal: { reason: 'severalPlans', fileNames: plans.map((file) => file.name) },
    };
  }

  const [plan] = plans;
  const creatives = files.filter((file) => !isPlan(file));

  const clashing = clashingNames(creatives);
  if (clashing.length > 0) {
    return { ok: false, refusal: { reason: 'clashingNames', fileNames: clashing } };
  }

  return tooLarge(files) ?? { ok: true, selection: { plan, creatives } };
}

/**
 * Names that more than one creative would be stored under.
 *
 * media-api keys a creative on `assetKey` — the base name, trimmed and lower-cased — so two
 * files that differ only in case, or that came from different folders, are one asset: the
 * second confirm displaces the first and the displaced object is then deleted. Both would
 * upload in full before that happened, and nothing would say which one survived.
 *
 * Refused rather than renamed. The plan's Image file column can only name one of them, so
 * renaming the second in silence would store an asset no row ever points at; the operator
 * has to decide which file the row means, and only they can.
 */
function clashingNames(creatives: File[]): string[] {
  const byKey = new Map<string, string[]>();

  for (const file of creatives) {
    const key = file.name.trim().toLowerCase();
    const names = byKey.get(key);
    if (names) names.push(file.name);
    else byKey.set(key, [file.name]);
  }

  // How many files share a key is what makes it a clash; which spellings they used is what
  // the operator has to go and find. Naming only the first left them reading "more than one
  // creative is called Hero.JPG" against a folder holding one `Hero.JPG` and one `hero.jpg`,
  // with nothing to rename. Deduplicated within the group so two files genuinely called the
  // same thing, out of different folders, are still named once.
  return [...byKey.values()]
    .filter((names) => names.length > 1)
    .flatMap((names) => [...new Set(names)]);
}

function tooLarge(files: File[]): { ok: false; refusal: Refusal } | undefined {
  const over = files.flatMap((file) => {
    const limitBytes = limitFor(file);
    return limitBytes !== undefined && file.size > limitBytes
      ? [{ fileName: file.name, limitBytes }]
      : [];
  });
  return over.length > 0 ? { ok: false, refusal: { reason: 'tooLarge', files: over } } : undefined;
}
